/**
 * Playable content for every stage, read from the theme (Admin → Game Content),
 * plus the whole-run plan used by the progress tracker and the results screen.
 */
import { ensureThemeJsonArray } from "../../functions/themeAssets";
import {
  buildWordsFromPairs,
  generateLayout,
  serializeCrosswordLayout,
} from "../../admin/pages/themeupdate/crosswordLayout";
import { getDragDropQuestions, normalizeCategoryName, shuffleCopy } from "./dragDropUtils";

const AUDIO_OPTION_KEYS = ["option_one", "option_two", "option_three", "option_four"];

/** Stage 1 — image quiz questions. */
export function getQuizQuestions(themeData) {
  return getDragDropQuestions(themeData);
}

function parseLayout(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Stage 2 — the admin-saved crossword grid, or one built from the Q&A pairs. */
export function getCrossword(themeData) {
  const saved = parseLayout(themeData?.crossword_layout);
  if (saved?.table?.length && Array.isArray(saved.words) && saved.words.length) return saved;

  const pairs = ensureThemeJsonArray(themeData?.questions_and_answers).map((p) => ({
    question: String(p?.question ?? ""),
    answer: String(p?.answer ?? ""),
  }));
  const words = buildWordsFromPairs(pairs);
  if (words.length < 2) return null;
  try {
    return serializeCrosswordLayout(generateLayout(words));
  } catch {
    return null;
  }
}

/** Placed crossword words with their grid cells (0-based [row, col]). */
export function getCrosswordWords(layout) {
  return (layout?.words || [])
    .filter((w) => w && (w.orientation === "across" || w.orientation === "down"))
    .map((w, index) => {
      const answer = String(w.answer || "").toLowerCase();
      const r0 = w.starty - 1;
      const c0 = w.startx - 1;
      return {
        ...w,
        index,
        answer,
        cells: Array.from(answer, (_, k) =>
          w.orientation === "across" ? [r0, c0 + k] : [r0 + k, c0]
        ),
      };
    });
}

/** Stage 3 — audio questions: a clip, 2+ options and a correct answer among them. */
export function getAudioQuestions(themeData) {
  return ensureThemeJsonArray(themeData?.audio_quiz)
    .map((row, i) => {
      const audio = String(row?.audio_file ?? "").trim();
      const options = AUDIO_OPTION_KEYS.map((k) => String(row?.[k] ?? "").trim()).filter(Boolean);
      const answerKey = normalizeCategoryName(row?.correct_answer);
      const correctAnswer = options.find((o) => normalizeCategoryName(o) === answerKey);
      if (!audio || options.length < 2 || !correctAnswer) return null;
      return { id: `audio-${i}`, audio, options, correctAnswer };
    })
    .filter(Boolean);
}

/** Stage 4 — image + name pairs. */
export function getMatchPairs(themeData) {
  return ensureThemeJsonArray(themeData?.match_pairs)
    .map((p, i) => ({
      id: i,
      image: String(p?.image ?? "").trim(),
      answer: String(p?.answer ?? "").trim(),
    }))
    .filter((p) => p.image && p.answer);
}

/** Shuffle the names so they never line up 1:1 with the pictures. */
export function shuffleMatchAnswers(pairs) {
  if (pairs.length < 2) return pairs.map((p) => p.id);
  let order = pairs.map((p) => p.id);
  for (let attempt = 0; attempt < 6; attempt++) {
    order = shuffleCopy(order);
    if (order.some((id, i) => id !== pairs[i].id)) break;
  }
  return order;
}

export function getPointsPerCorrect(themeData) {
  return Math.max(1, parseInt(themeData?.correctPoints ?? themeData?.points, 10) || 1);
}

/**
 * Whole-run plan. Each quiz / audio question is one progress step; the crossword
 * and the match board are one step each (as in the reference design).
 */
export function getRunPlan(themeData) {
  const quiz = getQuizQuestions(themeData).length;
  const words = getCrosswordWords(getCrossword(themeData)).length;
  const audio = getAudioQuestions(themeData).length;
  const pairs = getMatchPairs(themeData).length;

  const stageSteps = [quiz, words ? 1 : 0, audio, pairs ? 1 : 0];
  const stageOffsets = stageSteps.map((_, i) =>
    stageSteps.slice(0, i).reduce((sum, n) => sum + n, 0)
  );
  const totalSteps = stageSteps.reduce((sum, n) => sum + n, 0);
  const maxScore = (quiz + words + audio + pairs) * getPointsPerCorrect(themeData);

  return { stageSteps, stageOffsets, totalSteps, maxScore };
}

/** "Best New Year Parties" → ["Best ", <gold>"New Year"</gold>, " Parties"] parts. */
export function splitHighlight(text, pattern = /(new\s+year)/i) {
  return String(text || "")
    .split(pattern)
    .filter((part) => part !== "")
    .map((part) => ({ text: part, highlight: pattern.test(part) }));
}
